import {
  INVOICE_STATUS_TITLES,
  type Invoice,
  PAYMENT_METHOD_TITLES,
  PAYMENT_METHODS,
  type PaymentMethod,
  type Plan,
  type SellerSettings,
  type TenantSummary,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { date } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Panel } from '../../ui/layout';
import { plansQuery, tenantsQuery, todayIso } from './shared';

const invoicesQuery = (status: string) => ({
  queryKey: ['platform', 'invoices', status],
  queryFn: () => api<Invoice[]>('/platform/invoices', { query: { status } }),
});
const sellerQuery = {
  queryKey: ['platform', 'seller'],
  queryFn: () => api<SellerSettings | null>('/platform/seller'),
};

function money(v: number, currency: string): string {
  return `${v.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function invoiceBadge(inv: Invoice) {
  if (inv.status === 'paid') return <Badge tone="success">{INVOICE_STATUS_TITLES.paid}</Badge>;
  if (inv.status === 'cancelled') return <Badge>{INVOICE_STATUS_TITLES.cancelled}</Badge>;
  return inv.overdue ? (
    <Badge tone="danger">{INVOICE_STATUS_TITLES.overdue}</Badge>
  ) : (
    <Badge tone="warning">{INVOICE_STATUS_TITLES.issued}</Badge>
  );
}

/** Выставление счёта компании (ЛЦ-06): тариф, период, заметка. Компания фиксирована или выбирается. */
export function IssueInvoiceDialog({
  tenantId,
  tenants,
  plans,
  onClose,
}: {
  tenantId?: string;
  tenants?: TenantSummary[];
  plans: Plan[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [tid, setTid] = useState(tenantId ?? '');
  const [planId, setPlanId] = useState(plans.find((p) => p.isActive)?.id ?? plans[0]?.id ?? '');
  const [period, setPeriod] = useState<'month' | 'year'>('year');
  const [months, setMonths] = useState('1');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () =>
      api<Invoice>(`/platform/tenants/${tid}/invoices`, {
        body: {
          planId,
          period,
          months: period === 'year' ? 12 : Number(months) || 1,
          note: note.trim() || null,
        },
      }),
    onSuccess: (inv) => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast(`Счёт ${inv.number} выставлен`);
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    save.mutate();
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Выставить счёт"
      description="Сумма считается по цене тарифа и налогу страны компании; реквизиты продавца берутся из настроек."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="invoice-form"
            loading={save.isPending}
            disabled={!tid || !planId}
          >
            Выставить
          </Button>
        </>
      }
    >
      <form id="invoice-form" onSubmit={submit} className="grid gap-4" noValidate>
        {!tenantId ? (
          <Field label="Компания" htmlFor="inv-tenant">
            <Select id="inv-tenant" value={tid} onChange={(e) => setTid(e.target.value)}>
              <option value="">— выберите —</option>
              {(tenants ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Тариф" htmlFor="inv-plan" error={errors.planId}>
          <Select id="inv-plan" value={planId} onChange={(e) => setPlanId(e.target.value)}>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.isActive ? '' : ' (снят с продажи)'}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Период оплаты" htmlFor="inv-period">
          <Select
            id="inv-period"
            value={period}
            onChange={(e) => setPeriod(e.target.value === 'month' ? 'month' : 'year')}
          >
            <option value="year">На год</option>
            <option value="month">Помесячно</option>
          </Select>
        </Field>
        {period === 'month' ? (
          <Field label="Сколько месяцев" htmlFor="inv-months" error={errors.months}>
            <Input
              id="inv-months"
              type="number"
              inputMode="numeric"
              min={1}
              max={12}
              value={months}
              onChange={(e) => setMonths(e.target.value)}
            />
          </Field>
        ) : null}
        <Field label="Заметка" htmlFor="inv-note" hint="Видна в счёте и в выгрузке" error={errors.note}>
          <Textarea id="inv-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <ErrorBox error={save.error && !Object.keys(errors).length ? save.error : null} />
      </form>
    </Dialog>
  );
}

function PaymentDialog({ inv, onClose }: { inv: Invoice; onClose: () => void }) {
  const qc = useQueryClient();
  const left = Math.max(0, Math.round((inv.amountTotal - inv.paidTotal) * 100) / 100);
  const [amount, setAmount] = useState(String(left));
  const [paidOn, setPaidOn] = useState(todayIso());
  const [method, setMethod] = useState<PaymentMethod>('bank');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () =>
      api<Invoice>(`/platform/invoices/${inv.id}/payments`, {
        body: {
          amount: Number(amount.replace(',', '.')),
          paidOn,
          method,
          reference: reference.trim() || null,
          note: note.trim() || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast('Оплата записана');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Оплата счёта ${inv.number}`}
      description={`К оплате ${money(inv.amountTotal, inv.currency)}, уже оплачено ${money(inv.paidTotal, inv.currency)}. Когда оплачена вся сумма, лицензия продлевается.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="pay-form" loading={save.isPending}>
            Записать оплату
          </Button>
        </>
      }
    >
      <form
        id="pay-form"
        className="grid gap-4 sm:grid-cols-2"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setErrors({});
          save.mutate();
        }}
      >
        <Field label={`Сумма, ${inv.currency}`} htmlFor="pay-amount" error={errors.amount}>
          <Input
            id="pay-amount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label="Дата оплаты" htmlFor="pay-date" error={errors.paidOn}>
          <Input id="pay-date" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </Field>
        <Field label="Способ" htmlFor="pay-method">
          <Select id="pay-method" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_TITLES[m]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Номер платёжки" htmlFor="pay-ref" error={errors.reference}>
          <Input id="pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <Field label="Заметка" htmlFor="pay-note" className="sm:col-span-2" error={errors.note}>
          <Input id="pay-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <ErrorBox
          className="sm:col-span-2"
          error={save.error && !Object.keys(errors).length ? save.error : null}
        />
      </form>
    </Dialog>
  );
}

function InvoiceCard({ inv, onPay }: { inv: Invoice; onPay: () => void }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const cancel = useMutation({
    mutationFn: () => api<Invoice>(`/platform/invoices/${inv.id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      setConfirm(false);
      toast(`Счёт ${inv.number} отменён`);
    },
  });
  return (
    <li className="px-5 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium text-ink">{inv.number}</span>
        {invoiceBadge(inv)}
        <span className="text-[14px] text-ink [overflow-wrap:anywhere]">{inv.tenantName ?? '—'}</span>
        <span className="num ml-auto text-[14px] font-medium text-ink">
          {money(inv.amountTotal, inv.currency)}
        </span>
      </div>
      <div className="mt-1 text-[13px] text-ink-2">
        {inv.planName} · {inv.period === 'year' ? 'год' : `${inv.months} мес.`} · выставлен{' '}
        {date(inv.issuedOn)}, оплатить до {date(inv.dueOn)}
        {inv.paidOn ? `, оплачен ${date(inv.paidOn)}` : ''}
        {inv.requestedBy === 'company' ? ' · по запросу компании' : ''}
      </div>
      {inv.payments.length ? (
        <ul className="mt-1 text-[13px] text-ink-2">
          {inv.payments.map((p) => (
            <li key={p.id} className="num">
              {date(p.paidOn)}: {money(p.amount, inv.currency)},{' '}
              {PAYMENT_METHOD_TITLES[p.method].toLowerCase()}
              {p.reference ? `, платёжка ${p.reference}` : ''}
            </li>
          ))}
        </ul>
      ) : null}
      {inv.status === 'issued' ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant="primary" onClick={onPay}>
            Записать оплату
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setConfirm(true)}>
            Отменить счёт
          </Button>
        </div>
      ) : null}
      <Dialog
        open={confirm}
        onOpenChange={(v) => !v && setConfirm(false)}
        width="sm"
        title={`Отменить счёт ${inv.number}?`}
        description="Отменённый счёт оплатить нельзя; номер остаётся занятым."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(false)}>
              Не отменять
            </Button>
            <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
              Отменить счёт
            </Button>
          </>
        }
      >
        <ErrorBox error={cancel.error} />
      </Dialog>
    </li>
  );
}

const SELLER_TEXT: Array<[keyof SellerSettings, string, string?]> = [
  ['name', 'Название продавца'],
  ['countryCode', 'Страна (2 буквы)'],
  ['taxId', 'БИН / ИНН'],
  ['address', 'Адрес'],
  ['bank', 'Банк'],
  ['account', 'Счёт (IBAN)'],
  ['bankCode', 'БИК / код банка'],
  ['extra', 'Код назначения платежа и прочее'],
  ['signer', 'Кто подписывает'],
  ['email', 'Почта для счетов'],
  ['invoicePrefix', 'Префикс номера', 'СЧ → СЧ-2026-0001'],
  ['taxCode', 'Код налога', 'Пусто — без налога'],
];

function SellerPanel() {
  const qc = useQueryClient();
  const q = useQuery(sellerQuery);
  const [open, setOpen] = useState(false);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const s = q.data;
  return (
    <>
      <Panel
        title="Реквизиты продавца"
        meta="ЛЦ-06"
        actions={
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            {s ? 'Изменить' : 'Заполнить'}
          </Button>
        }
      >
        {s ? (
          <KeyValue
            items={[
              { label: 'Продавец', value: s.name },
              { label: 'БИН / ИНН', value: s.taxId || '—' },
              { label: 'Банк', value: [s.bank, s.account].filter(Boolean).join(', ') || '—' },
              { label: 'Номер счёта', value: `${s.invoicePrefix}-ГГГГ-0001` },
              { label: 'Срок оплаты', value: `${s.dueDays} дн.` },
              { label: 'Налог', value: s.taxCode || 'без налога' },
            ]}
          />
        ) : (
          <p className="text-[14px] text-ink-2">Реквизиты не заполнены — без них выставить счёт нельзя.</p>
        )}
      </Panel>
      {open ? (
        <SellerDialog
          seller={s}
          onClose={() => setOpen(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['platform'] })}
        />
      ) : null}
    </>
  );
}

function SellerDialog({
  seller,
  onClose,
  onSaved,
}: {
  seller: SellerSettings | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState<Record<string, string>>(() => ({
    name: seller?.name ?? '',
    countryCode: seller?.countryCode ?? 'KZ',
    taxId: seller?.taxId ?? '',
    address: seller?.address ?? '',
    bank: seller?.bank ?? '',
    account: seller?.account ?? '',
    bankCode: seller?.bankCode ?? '',
    extra: seller?.extra ?? '',
    signer: seller?.signer ?? '',
    email: seller?.email ?? '',
    invoicePrefix: seller?.invoicePrefix ?? 'INV',
    dueDays: String(seller?.dueDays ?? 10),
    taxCode: seller?.taxCode ?? 'VAT',
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      api('/platform/seller', { method: 'PUT', body: { ...f, dueDays: Number(f.dueDays) || 10 } }),
    onSuccess: () => {
      onSaved();
      toast('Реквизиты сохранены');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Реквизиты продавца"
      description="Попадают в каждый новый счёт; в выставленных счетах остаются прежние."
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="seller-form" loading={save.isPending}>
            Сохранить
          </Button>
        </>
      }
    >
      <form
        id="seller-form"
        className="grid gap-4 sm:grid-cols-2"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setErrors({});
          save.mutate();
        }}
      >
        {SELLER_TEXT.map(([key, label, hint]) => (
          <Field key={key} label={label} htmlFor={`sl-${key}`} hint={hint} error={errors[key]}>
            <Input
              id={`sl-${key}`}
              value={f[key] ?? ''}
              onChange={(e) => setF((x) => ({ ...x, [key]: e.target.value }))}
              invalid={!!errors[key]}
            />
          </Field>
        ))}
        <Field label="Срок оплаты, дней" htmlFor="sl-due" error={errors.dueDays}>
          <Input
            id="sl-due"
            type="number"
            inputMode="numeric"
            min={1}
            max={90}
            value={f.dueDays}
            onChange={(e) => setF((x) => ({ ...x, dueDays: e.target.value }))}
          />
        </Field>
        <ErrorBox
          className="sm:col-span-2"
          error={save.error && !Object.keys(errors).length ? save.error : null}
        />
      </form>
    </Dialog>
  );
}

/** Счета и оплаты лицензий (ЛЦ-06): кабинет владельца портала */
export function InvoicesPage() {
  const [status, setStatus] = useState('all');
  const [issuing, setIssuing] = useState(false);
  const [paying, setPaying] = useState<Invoice | null>(null);
  const [from, setFrom] = useState(() => `${todayIso().slice(0, 4)}-01-01`);
  const [to, setTo] = useState(todayIso());
  const q = useQuery(invoicesQuery(status));
  const plans = useQuery(plansQuery);
  const tenants = useQuery(tenantsQuery);

  return (
    <div className="flex flex-col gap-5">
      <SellerPanel />
      <Panel
        title="Счета"
        meta="ЛЦ-06"
        actions={
          <Button
            size="sm"
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setIssuing(true)}
            disabled={!plans.data || !tenants.data}
          >
            Выставить счёт
          </Button>
        }
        bodyClassName="p-0"
      >
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-5 py-3">
          <Field label="Показать" htmlFor="inv-filter" className="min-w-0 flex-1 sm:max-w-[220px]">
            <Select id="inv-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">Все</option>
              <option value="issued">{INVOICE_STATUS_TITLES.issued}</option>
              <option value="paid">{INVOICE_STATUS_TITLES.paid}</option>
              <option value="cancelled">{INVOICE_STATUS_TITLES.cancelled}</option>
            </Select>
          </Field>
        </div>
        {q.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : q.isError ? (
          <div className="p-5">
            <ErrorBox error={q.error} />
          </div>
        ) : !q.data.length ? (
          <div className="p-5">
            <EmptyState title="Счетов нет">Выставьте счёт на странице компании или здесь.</EmptyState>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {q.data.map((inv) => (
              <InvoiceCard key={inv.id} inv={inv} onPay={() => setPaying(inv)} />
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="Выгрузка для 1С" meta="CSV">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="С" htmlFor="exp-from">
            <Input id="exp-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="По" htmlFor="exp-to">
            <Input id="exp-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <a
            href={`/api/v1/platform/invoices/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`}
            download
            className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-line bg-surface px-4 text-[14px] font-medium text-ink hover:bg-surface-2 md:h-10"
          >
            <Download className="size-4" aria-hidden />
            Скачать CSV
          </a>
        </div>
      </Panel>
      {issuing && plans.data && tenants.data ? (
        <IssueInvoiceDialog tenants={tenants.data} plans={plans.data} onClose={() => setIssuing(false)} />
      ) : null}
      {paying ? <PaymentDialog inv={paying} onClose={() => setPaying(null)} /> : null}
    </div>
  );
}
